"use client";

import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchRendezvousByMedecin,
  confirmRendezvous,
  cancelRendezvous,
  rescheduleRendezvous,
} from "../redux/thunks/rendezvousThunks";
import { axiosSousProfile } from "../../api/axios";
import { SOUS_PROFILE_PATHS } from "../../api/endpoints";
import { fetchUsers } from "../../Profile/redux/thunks/userThunks";
import { fetchLocalPatients } from "../../WaitingRoom/redux/thunks/localPatient.thunks";
import {
  selectAllLocalPatients,
  selectLocalPatientsLoaded,
  selectLocalPatientsLoading,
} from "../../WaitingRoom/redux/selectors/localPatient.selectors";
import { fetchRoomsByDoctor } from "../../WaitingRoom/redux/thunks/room.thunks";
import { selectAllRooms } from "../../WaitingRoom/redux/selectors/room.selectors";

/* ---------------- Utils ---------------- */

const arr = (x) =>
  Array.isArray(x) ? x : Array.isArray(x?.items) ? x.items : [];

const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("fr-FR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      })
    : "";

const fmtTime = (iso) =>
  iso
    ? new Date(iso).toLocaleTimeString("fr-FR", {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

const buildUsersIndex = (users) => {
  const byId = new Map();
  const byUuid = new Map();
  for (const u of users) {
    if (u?.id != null)       byId.set(String(u.id), u);
    if (u?.userId != null)   byId.set(String(u.userId), u);
    if (u?._id != null)      byId.set(String(u._id), u);
    if (u?.uuid)             byUuid.set(String(u.uuid), u);
    if (u?.externalId)       byUuid.set(String(u.externalId), u);
  }
  return { byId, byUuid };
};

const buildPatientIndex = (patients) => {
  const byId = new Map();
  const byUuid = new Map();
  for (const p of patients) {
    if (!p) continue;
    const keys = [p.id, p._id, p.patientId, p.userId, p.uuid, p.externalId];
    for (const key of keys) {
      if (key != null) byId.set(String(key), p);
    }
    if (p?.uuid)       byUuid.set(String(p.uuid), p);
    if (p?.externalId) byUuid.set(String(p.externalId), p);
  }
  return { byId, byUuid };
};

const pickName = (u) =>
  u?.name ||
  u?.fullName ||
  [u?.firstName, u?.lastName].filter(Boolean).join(" ") ||
  [u?.first_name, u?.last_name].filter(Boolean).join(" ") ||
  "";

const pickIdentifier = (u) =>
  u?.identifier || u?.cin || u?.n_assurance || u?.externalId || u?.uuid || "";

const getPatientIdKeys = (r) =>
  [
    r.patientId, r.patient?.id, r.patient?.userId,
    r.patientUserId, r.patientExternalId, r.patientUUID,
    r.patient?._id, r.patient?.uuid, r.userId,
  ].filter(Boolean).map(String);

const pickSubprofileId = (r) => {
  const value =
    r?.subprofileId ??
    r?.subProfileId ??
    r?.sousProfileId ??
    r?.subprofile?.id ??
    r?.subprofile?._id ??
    r?.subProfile?.id ??
    r?.effectiveProfileId;

  if (!value) return "";
  const clean = String(value).trim();
  const patientId = String(r?.patientId ?? r?.userId ?? "").trim();
  // A subprofileId equal to the doctor's own id is corrupted data (a stray
  // acting-subprofile header leaking into a doctor-created appointment),
  // never a real sub-profile — ignore it and fall back to the patient.
  const doctorId = String(r?.medecinId ?? r?.doctorId ?? "").trim();
  if (clean === doctorId) return "";
  return clean && clean !== patientId ? clean : "";
};

const pickSubprofileName = (r, subprofile) =>
  pickName(subprofile) ||
  pickName(r?.subprofile) ||
  pickName(r?.subProfile) ||
  r?.subprofileName ||
  r?.subProfileName ||
  r?.sousProfileName ||
  "";

const unwrapPayload = (payload) => payload?.data ?? payload;

/* Signature de contenu tolérante : si la sérialisation échoue (structure
   circulaire venue de l'API), on renvoie null → la comparaison échoue et on
   retombe sur l'ancien comportement (mise à jour du state). */
const contentSignature = (value) => {
  try {
    return JSON.stringify(value);
  } catch {
    return null;
  }
};

const enrichRowWithUser = (r, usersIdx, localIdx, subprofilesById) => {
  let u = null;
  let matchedLocal = false;
  const subprofileId = pickSubprofileId(r);
  const subprofile = subprofileId ? subprofilesById?.[subprofileId] : null;
  const subprofileName = subprofileId ? pickSubprofileName(r, subprofile) : "";
  for (const k of getPatientIdKeys(r)) {
    if (usersIdx.byId.has(k))   { u = usersIdx.byId.get(k);   break; }
    if (usersIdx.byUuid.has(k)) { u = usersIdx.byUuid.get(k); break; }
    if (localIdx.byId.has(k))   { u = localIdx.byId.get(k);   matchedLocal = true; break; }
    if (localIdx.byUuid.has(k)) { u = localIdx.byUuid.get(k); matchedLocal = true; break; }
  }
  const fallbackPhoto = r.patient?.avatar || r.patient?.photo || null;
  const principalPatientName =
    pickName(u) || r.patient?.fullName || r.patientName || r.patient?.name || "";

  return {
    id: r.id ?? r._id,
    name:
      subprofileName ||
      principalPatientName ||
      "Patient",
    principalPatientName,
    // A sub-profile has no account of its own, so the parent account's email
    // must never be shown as "their" email on the doctor's card.
    email: subprofileId
      ? subprofile?.email ?? ""
      : u?.email ?? r.patient?.email ?? r.patientEmail ?? "",
    phone: u?.phone ?? r.patient?.phone ?? r.patientPhone ?? "",
    // Likewise, don't show the parent's own photo on a sub-profile's card —
    // fall back to the generic placeholder until the sub-profile has one set.
    photo: subprofileId
      ? subprofile?.avatarUrl ?? subprofile?.avatar ?? subprofile?.photo ?? null
      : u?.avatarUrl ?? u?.avatar ?? fallbackPhoto,
    identifier:
      pickIdentifier(u) || r.patient?.identifier || r.patientIdentifier || "",
    state: r.patient?.isNew || matchedLocal ? "New patient" : "Existing",
    reason: r.reason ?? r.motif ?? r.appointmentReason ?? "",
    date: fmtDate(r.startTime || r.date),
    time: fmtTime(r.startTime || r.date),
    status: r.status || "scheduled",
    startTime: r.startTime,
    endTime: r.endTime,
    medecinId: r.medecinId ?? r.doctorId,
    patientId: r.patientId ?? r.userId,
    subprofileId,
    effectiveProfileId:
      r.effectiveProfileId ?? (subprofileId || r.patientId || r.userId),
    type: r.type || "standard",
    source: r.source || (subprofileId ? "subprofile" : matchedLocal ? "local" : u ? "medilink" : "local"),
    raw: r,
  };
};

const pickDoctorId = (doctorSlice) => {
  const u = doctorSlice?.user || doctorSlice?.currentUser || {};
  return u?.doctorId || u?.medecinId || u?.id || u?.userId || u?._id || null;
};

/* ---------------- LocalStorage Overrides ---------------- */

const STORAGE_KEY = "medilink_rdv_overrides";

const readOverrides = () => {
  try {
    if (typeof window === "undefined") return {};
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch { return {}; }
};

const writeOverrides = (data) => {
  try {
    if (typeof window === "undefined") return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {}
};

let globalStatusOverrides = readOverrides();

/* ---------------- Hook ---------------- */

export default function useDoctorPendingRdv(medecinIdProp, options = {}) {
  const { futureOnly = false, disableRemote = false, localItems = [] } = options;

  const dispatch = useDispatch();

  const doctorSlice       = useSelector((s) => s.doctor);
  const doctorIdFromStore = pickDoctorId(doctorSlice);
  const medecinId         = medecinIdProp ?? doctorIdFromStore;

  const storeItems     = useSelector((s) => s.rendezvous.byMedecin.items);
  const loading        = useSelector((s) => s.rendezvous.byMedecin.loading);
  const error          = useSelector((s) => s.rendezvous.byMedecin.error);
  const usersById      = useSelector((s) => s.user.usersById);
  const usersLoading   = useSelector((s) => s.user.loading);
  const localPatients  = useSelector(selectAllLocalPatients);
  const localPatientsLoaded  = useSelector(selectLocalPatientsLoaded);
  const localPatientsLoading = useSelector(selectLocalPatientsLoading);
  const rooms = useSelector(selectAllRooms);
  const [subprofilesById, setSubprofilesById] = useState({});

  const usersArray        = useMemo(() => Object.values(usersById || {}), [usersById]);
  const localPatientsArray = useMemo(() => localPatients || [], [localPatients]);
  const subprofileIds = useMemo(
    () => Array.from(new Set(arr(storeItems).map(pickSubprofileId).filter(Boolean))),
    [storeItems]
  );

  /* ⚠️ `localItems` arrive presque toujours comme un tableau littéral neuf
     (prop par défaut `localItems = []`, ou `localItems={[]}` chez
     l'assistant) : son IDENTITÉ change à chaque render. Le mettre tel quel
     dans les deps de `enriched` recréait `enriched` à chaque render, ce qui
     relançait l'effet de synchro `setItems(...)` → nouveau render → ...
     boucle infinie. On dépend donc de son CONTENU. */
  const localItemsKey = useMemo(
    () => (disableRemote ? String(contentSignature(arr(localItems))) : ""),
    [disableRemote, localItems]
  );

  const reload = useCallback(() => {
    if (disableRemote) return Promise.resolve();
    if (!medecinId) return;
    return Promise.all([
      dispatch(fetchRendezvousByMedecin({ userId: medecinId, query: {} })),
      dispatch(fetchRoomsByDoctor({ doctorId: medecinId })),
    ]);
  }, [dispatch, medecinId, disableRemote]);

  useEffect(() => {
    if (disableRemote) return;
    reload();
  }, [reload, disableRemote]);

  useEffect(() => {
    if (disableRemote) return;
    if (!usersArray.length) dispatch(fetchUsers());
  }, [dispatch, usersArray.length, disableRemote]);

  useEffect(() => {
    if (disableRemote) return;
    if (!localPatientsLoaded && !localPatientsLoading)
      dispatch(fetchLocalPatients({ activeOnly: true }));
  }, [dispatch, localPatientsLoaded, localPatientsLoading, disableRemote]);

  useEffect(() => {
    if (disableRemote) return;
    const missingIds = subprofileIds.filter((id) => !(id in subprofilesById));
    if (!missingIds.length) return;

    let cancelled = false;

    Promise.all(
      missingIds.map(async (id) => {
        try {
          const { data } = await axiosSousProfile.get(SOUS_PROFILE_PATHS.GET_BY_ID(id), {
            withCredentials: true,
          });
          return [id, unwrapPayload(data)];
        } catch {
          return [id, null];
        }
      })
    ).then((entries) => {
      if (cancelled) return;
      setSubprofilesById((prev) => {
        const next = { ...prev };
        entries.forEach(([id, profile]) => {
          next[id] = profile;
        });
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [subprofileIds, subprofilesById, disableRemote]);

  const usersIndex = useMemo(() => buildUsersIndex(usersArray), [usersArray]);
  const localIndex = useMemo(() => buildPatientIndex(localPatientsArray), [localPatientsArray]);

  const enriched = useMemo(() => {
    let base = disableRemote
      ? arr(localItems).map((item) => ({
          id: item.id,
          name: item.name,
          email: item.email || "",
          phone: item.phone || "",
          photo: item.photo || null,
          identifier: item.identifier || "",
          state: item.state || "Existing",
          reason: item.reason || "",
          date: item.date || "",
          time: item.time || "",
          status: item.status || "scheduled",
          startTime: item.startTime || null,
          endTime: item.endTime || null,
          medecinId: medecinId || null,
          patientId: item.patientId || item.id,
          subprofileId: "",
          effectiveProfileId: item.patientId || item.id,
          type: item.type || "standard",
          source: item.source || "local",
          raw: item,
        }))
      : (() => {
        const appointmentRows = arr(storeItems);
        const appointmentIds = new Set(
          appointmentRows
            .map((appointment) => appointment?.id ?? appointment?._id)
            .filter((id) => id != null)
            .map(String),
        );
        const roomOnlyRows = rooms
          .filter(
            (room) =>
              room?.appointmentId != null &&
              !appointmentIds.has(String(room.appointmentId)),
          )
          .map((room) => ({
            id: room.appointmentId,
            patientId: room.patientId,
            medecinId: room.doctorId ?? medecinId,
            status: room.status,
            startTime: room.createdAt ?? null,
            endTime: room.updatedAt ?? null,
          }));

        return [...appointmentRows, ...roomOnlyRows].map((r) => {
          const enrichedRow = enrichRowWithUser(r, usersIndex, localIndex, subprofilesById);
          const room = rooms.find(
            (candidate) => String(candidate?.appointmentId) === String(r?.id ?? r?._id)
          );

          return room?.status === "completed"
            ? { ...enrichedRow, status: "completed", raw: { ...enrichedRow.raw, status: "completed" } }
            : enrichedRow;
        });
      })();
    if (futureOnly) {
      const now = Date.now();
      base = base.filter((r) => {
        const t = r.startTime ? new Date(r.startTime).getTime() : NaN;
        return !Number.isNaN(t) && t >= now;
      });
    }
    return base;
    // `localItems` est lu via `localItemsKey` (contenu) : voir plus haut.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeItems, usersIndex, localIndex, subprofilesById, rooms, futureOnly, disableRemote, localItemsKey, medecinId]);

  // ✅ applyOverrides: server data wins for "cancelled" — never let an old
  //    override resurrect a server-confirmed cancel.
  const applyOverrides = useCallback((serverItems) => {
    globalStatusOverrides = readOverrides();
    return serverItems.map((serverItem) => {
      const sid      = String(serverItem.id);
      const override = globalStatusOverrides[sid];
      if (!override) return serverItem;

      // If server already says "cancelled", honour it and clear the override
      // so it doesn't interfere with future status changes.
      if (serverItem.status === "cancelled") {
        delete globalStatusOverrides[sid];
        writeOverrides(globalStatusOverrides);
        return serverItem;
      }

      return {
        ...serverItem,
        ...override,
        raw: { ...serverItem.raw, ...override },
      };
    });
  }, []);

  const [items, setItems]       = useState(() => applyOverrides(enriched));
  const lastSnapshotRef         = useRef(null);

  useEffect(() => {
    const next = applyOverrides(enriched);
    // `applyOverrides` renvoie toujours un NOUVEAU tableau : sans cette
    // comparaison de contenu, React ne peut jamais bailout et le moindre
    // changement d'identité de `enriched` relance un render en boucle.
    const snapshot = contentSignature(next);
    if (snapshot !== null && lastSnapshotRef.current === snapshot) return;
    lastSnapshotRef.current = snapshot;
    setItems(next);
  }, [enriched, applyOverrides]);

  const updateItemStatus = useCallback((id, status, extraFields = {}) => {
    const sid = String(id);
    globalStatusOverrides = readOverrides();
    globalStatusOverrides[sid] = { status, ...extraFields };
    writeOverrides(globalStatusOverrides);
    setItems((prev) =>
      prev.map((x) =>
        String(x.id) === sid
          ? { ...x, status, ...extraFields, raw: { ...x.raw, status, ...extraFields } }
          : x
      )
    );
  }, []);

  const removeOverride = useCallback((id) => {
    const sid = String(id);
    globalStatusOverrides = readOverrides();
    if (globalStatusOverrides[sid]) {
      delete globalStatusOverrides[sid];
      writeOverrides(globalStatusOverrides);
    }
  }, []);

  // ✅ Cross-tab listener: patient cancels/reschedules → doctor view updates
  // Uses BroadcastChannel (cross-tab) + localStorage event (fallback)
  useEffect(() => {
    if (disableRemote) return;
    const BC_NAME = "medilink:appointments";
    const LS_KEY  = "medilink:appointment_action";

    const applyAction = ({ appointmentId, status, startTime, endTime }) => {
      if (!appointmentId || !status) return;

      // Write override so the UI flips immediately
      updateItemStatus(appointmentId, status, {
        ...(startTime ? { startTime } : {}),
        ...(endTime   ? { endTime }   : {}),
      });

      // Reload after short delay to let the server persist, then again as safety net
      setTimeout(() => {
        reload();
        setTimeout(() => reload(), 3000);
      }, 800);
    };

    // BroadcastChannel — works across tabs on the same origin
    let bc = null;
    try {
      bc = new BroadcastChannel(BC_NAME);
      bc.onmessage = (e) => applyAction(e.data || {});
    } catch (err) {
      console.warn("[useDoctorPendingRdv] BroadcastChannel not supported:", err);
    }

    // localStorage storage event — cross-tab fallback
    const handleStorage = (e) => {
      if (e.key !== LS_KEY || !e.newValue) return;
      try { applyAction(JSON.parse(e.newValue)); } catch {}
    };
    window.addEventListener("storage", handleStorage);

    // Same-tab CustomEvent fallback
    const handleLocal = (e) => applyAction(e.detail || {});
    window.addEventListener("appointment:patient_action", handleLocal);

    return () => {
      bc?.close();
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("appointment:patient_action", handleLocal);
    };
  }, [updateItemStatus, reload, disableRemote]);

  /* ── Doctor actions ── */

  const accept = useCallback(
    async (id) => {
      if (disableRemote) {
        updateItemStatus(id, "confirmed");
        return;
      }
      if (!medecinId) throw new Error("medecinId manquant");
      if (!id) throw new Error("rendezvousId manquant");
      lastSnapshotRef.current = items;
      updateItemStatus(id, "confirmed");
      try {
        await dispatch(confirmRendezvous({ userId: medecinId, id })).unwrap();
        await reload();
      } catch (e) {
        removeOverride(id);
        if (lastSnapshotRef.current) setItems(lastSnapshotRef.current);
        throw e;
      }
    },
    [dispatch, medecinId, items, updateItemStatus, reload, removeOverride, disableRemote]
  );

  const cancel = useCallback(
    async (id) => {
      if (disableRemote) {
        updateItemStatus(id, "cancelled");
        return;
      }
      if (!medecinId) throw new Error("medecinId manquant");
      if (!id) throw new Error("rendezvousId manquant");
      lastSnapshotRef.current = items;
      updateItemStatus(id, "cancelled");
      try {
        await dispatch(cancelRendezvous({ userId: medecinId, rendezvousId: id, by: "doctor" })).unwrap();
        await reload();
      } catch (e) {
        removeOverride(id);
        if (lastSnapshotRef.current) setItems(lastSnapshotRef.current);
        throw e;
      }
    },
    [dispatch, medecinId, items, updateItemStatus, reload, removeOverride, disableRemote]
  );

  const reschedule = useCallback(
    async (id, payload) => {
      if (disableRemote) {
        const startTime = payload?.startTime || payload?.date || payload?.newStartTime;
        const endTime = payload?.endTime || payload?.newEndTime;
        updateItemStatus(id, "rescheduled", {
          ...(startTime ? { startTime } : {}),
          ...(endTime ? { endTime } : {}),
          ...(startTime ? { date: fmtDate(startTime), time: fmtTime(startTime) } : {}),
        });
        return;
      }
      if (!medecinId) throw new Error("medecinId manquant");
      if (!id) throw new Error("rendezvousId manquant");
      lastSnapshotRef.current = items;
      const startTime = payload?.startTime || payload?.date || payload?.newStartTime;
      const endTime   = payload?.endTime   || payload?.newEndTime;
      if (!startTime) throw new Error("startTime manquant");
      if (!endTime)   throw new Error("endTime manquant");
      updateItemStatus(id, "rescheduled", {
        startTime, endTime,
        date: fmtDate(startTime),
        time: fmtTime(startTime),
      });
      try {
        await dispatch(rescheduleRendezvous({ userId: medecinId, rendezvousId: id, startTime, endTime, status: "rescheduled" })).unwrap();
        await reload();
      } catch (e) {
        removeOverride(id);
        if (lastSnapshotRef.current) setItems(lastSnapshotRef.current);
        throw e;
      }
    },
    [dispatch, medecinId, items, updateItemStatus, reload, removeOverride, disableRemote]
  );

  return {
    medecinId,
    items,
    loading: loading || usersLoading,
    error,
    reload,
    accept,
    cancel,
    decline: cancel,
    reschedule,
    updateItemStatus,
  };
}
